import { Observable } from 'lib0/observable';
import * as Y from 'yjs';

import { DisposableElement, WrappedYType, isDisposableElement } from './types';

export type LazyValueEvent = 'beforeDispose' | 'afterInitialize';

/**
 * Takes a value that maybe a LazyValue or a regular value and normalizes it into a LazyValue.
 * In case of a regular value, the resulting LazyValue will always be initialized.
 * This can be used in places that accept both lazy values and regular values, in order to
 * handle them in a unified way.
 */
export function normalizeLazyValue<T>(value: T | LazyValue<T>): LazyValue<T> {
  if (value instanceof LazyValue) {
    return value;
  } else {
    return new LazyValue<T>(
      () => value,
      () => value
    );
  }
}

/**
 * Makes sure that the given object has a method with the given property name, otherwise throws an exception.
 */
function ensureMethod<MethodName extends string>(
  object: any,
  methodName: MethodName
): object is Record<MethodName, (...args: any[]) => any> {
  if (object && typeof object === 'object' && typeof object[methodName] === 'function') {
    return true;
  } else {
    throw new Error(`Object does not implement method ${methodName}.`);
  }
}

/**
 * Represents a YJS element that may be undefined and is only initialized and attached when modifications
 * to it are applied.
 *
 * The "value" getter should be used when the contents of the element are read. It will return undefined
 * if the element is not initialized. The "valueRequired" getter should be used then the contents of the
 * element are modified. If the element is not initialized yet, it will be initialized then.
 *
 * Our YJS classes that need this functionality should extend this class and call the constructor with
 * the appropriate arguments.
 *
 * Observers can be attached to the underlying value. If the value is not initialized, they will be attached
 * lazily once the value is initialized. It should be noted that the observers are triggered only when
 * something inside the value changes, not when the value itself changes (due to it being initialized or
 * re-initialized). For that case, objects of this class emit an "afterInitialize" event that can be used
 * to listen to changes to the value itself.
 */
export class LazyValue<T>
  extends Observable<LazyValueEvent>
  implements LazyValue<T>, DisposableElement, WrappedYType
{
  #value: T | undefined;

  private observers = new Set<any>();
  private deepObservers = new Set<any>();

  /**
   * @param getValue Should return the value or undefined if it is not initialized yet.
   * @param initialize Should create an empty value and attach it to the doc. Will be called when the
   *   value is undefined and "valueRequired" is accessed.
   */
  constructor(private getValue: () => T | undefined, private initialize: () => T) {
    super();

    this.initializeValue(false);
  }

  /**
   * Returns the value or undefined if it is not initialized yet.
   */
  get value(): T | undefined {
    return this.#value;
  }

  /**
   * Returns the value If it is not initialized yet, it is initialized.
   */
  get valueRequired(): T {
    if (!this.#value) {
      this.initializeValue(true);
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.#value!;
  }

  /**
   * Initializes the value. If the value is already initialized, the existing value is
   * disposed.
   * @param force If this is true, if the new value is undefined, it will be initialized.
   *   Otherwise it is left undefined.
   */
  protected initializeValue(force: boolean) {
    if (this.#value) {
      this.emit('beforeDispose', []);

      this.unregisterObservers();
      this.unregisterDeepObservers();

      if (isDisposableElement(this.#value)) {
        this.#value.dispose();
      }
      this.#value = undefined;
    }

    this.#value = this.getValue();

    const shouldInitialize = !this.#value && force;
    if (shouldInitialize) {
      this.#value = this.initialize();
    }

    if (this.#value) {
      this.registerObservers();
      this.registerDeepObservers();
    }

    if (shouldInitialize) {
      this.emit('afterInitialize', []);
    }
  }

  dispose(): void {
    if (this.#value && isDisposableElement(this.#value)) {
      this.#value.dispose();
    }
  }

  /**
   * Attaches an observer to the value. If the value is not initialized, the observer will be lazily
   * attached when the value is initialized.
   * This can only be used if the underlying value also has an "observe" method. Otherwise and exception
   * is thrown.
   */
  observe(
    observer: T extends { observe(observer: infer ObserverType): void }
      ? ObserverType
      : never
  ): void {
    if (this.#value && ensureMethod(this.#value, 'observe')) {
      this.#value.observe(observer);
    }

    this.observers.add(observer);
  }

  unobserve(
    observer: T extends { unobserve(observer: infer ObserverType): void }
      ? ObserverType
      : never
  ): void {
    if (this.#value && ensureMethod(this.#value, 'unobserve')) {
      this.#value.unobserve(observer);
    }

    this.observers.delete(observer);
  }

  private registerObservers() {
    if (this.observers.size > 0 && ensureMethod(this.#value, 'observe')) {
      for (const observer of this.observers) {
        this.#value.observe(observer);
      }
    }
  }

  private unregisterObservers() {
    if (this.observers.size > 0 && ensureMethod(this.#value, 'unobserve')) {
      for (const observer of this.observers) {
        this.#value.unobserve(observer);
      }
    }
  }

  /**
   * Attaches a deep observer to the value. If the value is not initialized, the observer will be lazily
   * attached when the value is initialized.
   * This can only be used if the underlying value also has an "observeDeep" method. Otherwise and exception
   * is thrown.
   */
  observeDeep(
    observer: T extends { observeDeep(observer: infer ObserverType): void }
      ? ObserverType
      : never
  ): void {
    if (this.#value && ensureMethod(this.#value, 'observeDeep')) {
      this.#value.observeDeep(observer);
    }

    this.deepObservers.add(observer);
  }

  unobserveDeep(
    observer: T extends { unobserveDeep(observer: infer ObserverType): void }
      ? ObserverType
      : never
  ): void {
    if (this.deepObservers.size > 0 && ensureMethod(this.#value, 'unobserveDeep')) {
      this.#value.unobserveDeep(observer);
    }

    this.deepObservers.delete(observer);
  }

  private registerDeepObservers() {
    if (this.deepObservers.size > 0 && ensureMethod(this.#value, 'observeDeep')) {
      for (const observer of this.deepObservers) {
        this.#value.observeDeep(observer);
      }
    }
  }

  private unregisterDeepObservers() {
    if (this.deepObservers.size > 0 && ensureMethod(this.#value, 'unobserveDeep')) {
      for (const observer of this.deepObservers) {
        this.#value.unobserveDeep(observer);
      }
    }
  }

  unwrap(): Y.AbstractType<any> | undefined {
    if (
      this.value &&
      typeof this.value === 'object' &&
      this.value instanceof Y.AbstractType
    ) {
      return this.value;
    }
  }
}
